import { ApiVaultData, BaultPosition, FarmPosition, IslandPosition } from 'types/portfolio'
import { ProcessedV2Position, ProcessedV3Position } from 'types/subgraph'

export const normalizeDisplaySymbol = (symbol?: string): string => (symbol ?? '').replace(/\u20ae/g, 'T')

// Case-insensitive string matching utility
function matchesFilter(value: string | undefined, filter: string): boolean {
  if (!value || !filter) return true
  return value.toLowerCase().includes(filter.toLowerCase())
}

// Token holdings filter
export function filterTokenHoldings(tokens: any[], filter: string) {
  if (!filter) return tokens

  return tokens.filter(
    (tokenData) =>
      matchesFilter(tokenData.token?.symbol, filter) ||
      matchesFilter(tokenData.token?.name, filter) ||
      matchesFilter(tokenData.address, filter) ||
      matchesFilter(tokenData.token?.id, filter)
  )
}

// V3 positions filter
export function filterV3Positions(positions: ProcessedV3Position[], filter: string) {
  if (!filter) return positions

  return positions.filter(
    (position) =>
      matchesFilter(position.nftId, filter) ||
      matchesFilter(position.enrichedToken0?.symbol, filter) ||
      matchesFilter(position.enrichedToken0?.name, filter) ||
      matchesFilter(position.enrichedToken0?.id, filter) ||
      matchesFilter(position.enrichedToken1?.symbol, filter) ||
      matchesFilter(position.enrichedToken1?.name, filter) ||
      matchesFilter(position.enrichedToken1?.id, filter) ||
      matchesFilter(position.poolAddress, filter)
  )
}

// V2 positions filter
export function filterV2Positions(positions: ProcessedV2Position[], filter: string) {
  if (!filter) return positions

  return positions.filter(
    (position) =>
      matchesFilter(position.id, filter) ||
      matchesFilter(position.token0?.symbol, filter) ||
      matchesFilter(position.token0?.name, filter) ||
      matchesFilter(position.token0?.address, filter) ||
      matchesFilter(position.token1?.symbol, filter) ||
      matchesFilter(position.token1?.name, filter) ||
      matchesFilter(position.token1?.address, filter) ||
      matchesFilter(position.pairAddress, filter)
  )
}

// Island positions filter
export function filterIslandPositions(positions: IslandPosition[], filter: string) {
  if (!filter) return positions

  return positions.filter(
    (position) =>
      matchesFilter(position.vault?.id, filter) ||
      matchesFilter(position.vault?.token0?.symbol, filter) ||
      matchesFilter(position.vault?.token0?.name, filter) ||
      matchesFilter(position.vault?.token1?.symbol, filter) ||
      matchesFilter(position.vault?.token1?.name, filter) ||
      matchesFilter(position.vault?.tokenLp?.symbol, filter) ||
      matchesFilter(position.vault?.tokenLp?.name, filter)
  )
}

// Bault positions filter
export function filterBaultPositions(positions: BaultPosition[], filter: string) {
  if (!filter) return positions

  return positions.filter(
    (position) =>
      matchesFilter(position.vault?.id, filter) ||
      matchesFilter(position.bault?.id, filter) ||
      matchesFilter(position.vault?.token0?.symbol, filter) ||
      matchesFilter(position.vault?.token0?.name, filter) ||
      matchesFilter(position.vault?.token1?.symbol, filter) ||
      matchesFilter(position.vault?.token1?.name, filter) ||
      matchesFilter(position.vault?.tokenLp?.symbol, filter) ||
      matchesFilter(position.vault?.tokenLp?.name, filter)
  )
}

// Farm positions filter
export function filterFarmPositions(positions: FarmPosition[], filter: string) {
  if (!filter) return positions

  return positions.filter(
    (position) =>
      matchesFilter(position.farm?.id, filter) ||
      matchesFilter(position.vault?.id, filter) ||
      matchesFilter(position.vault?.token0?.symbol, filter) ||
      matchesFilter(position.vault?.token0?.name, filter) ||
      matchesFilter(position.vault?.token1?.symbol, filter) ||
      matchesFilter(position.vault?.token1?.name, filter) ||
      matchesFilter(position.vault?.tokenLp?.symbol, filter) ||
      matchesFilter(position.vault?.tokenLp?.name, filter) ||
      // Also filter by reward tokens
      position.claimableRewards?.some(
        (reward) => matchesFilter(reward.token?.symbol, filter) || matchesFilter(reward.token?.name, filter)
      )
  )
}

// Vaults filter (for AllPositions component - filters unique vaults)
export function filterVaults(vaults: ApiVaultData[], filter: string) {
  if (!filter) return vaults

  return vaults.filter(
    (vault) =>
      matchesFilter(vault.id, filter) ||
      matchesFilter(vault.token0?.symbol, filter) ||
      matchesFilter(vault.token0?.name, filter) ||
      matchesFilter(vault.token1?.symbol, filter) ||
      matchesFilter(vault.token1?.name, filter) ||
      matchesFilter(vault.tokenLp?.symbol, filter) ||
      matchesFilter(vault.tokenLp?.name, filter)
  )
}
